import React, { useEffect, useMemo, useRef, useState } from 'react';
import { EmployeeSummary } from '../types/models';
import {
  formatOvertimeDateTime, formatOvertimeMinutes, normalizeOvertimeReport,
  OVERTIME_STATUS_LABELS, type OvertimeClient, type OvertimeReport, type OvertimeReviewStatus
} from './overtimeApi';

export const OVERTIME_REPORT_COLUMNS = 'id,attendance_id,employee_id,employee_code,employee_name,work_date,start_at,end_at,duration_minutes,reason,status,admin_note,submitted_at,updated_at,reviewed_at,version';
const PAGE_SIZE = 100;

export const getOvertimePeriod = (month: string, closingDay: string): { start: string; end: string } | null => {
  if (!/^\d{4}-\d{2}$/.test(month)) return null;
  const [year, m] = month.split('-').map(Number);
  if (m < 1 || m > 12) return null;
  const format = (date: Date) => date.toISOString().slice(0, 10);
  if (closingDay === 'month-end') return { start: `${month}-01`, end: format(new Date(Date.UTC(year, m, 0))) };
  const day = Number(closingDay);
  if (!Number.isInteger(day) || day < 1 || day > 31) return null;
  // selectedMonth is the month containing the closing date, as in the dashboard.
  const end = new Date(Date.UTC(year, m - 1, Math.min(day, new Date(Date.UTC(year, m, 0)).getUTCDate())));
  const previousEnd = new Date(Date.UTC(year, m - 2, Math.min(day, new Date(Date.UTC(year, m - 1, 0)).getUTCDate())));
  return { start: format(new Date(previousEnd.getTime() + 86400000)), end: format(end) };
};

interface Props {
  supabaseClient: OvertimeClient | null;
  employees: EmployeeSummary[];
  selectedMonth: string;
  closingDay: string;
  onMonthChange?: (month: string) => void;
}

export const OvertimeReviewTab = ({ supabaseClient, employees, selectedMonth, closingDay, onMonthChange }: Props) => {
  const [employeeId, setEmployeeId] = useState('');
  const [status, setStatus] = useState<OvertimeReviewStatus | ''>('');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<OvertimeReport[]>([]);
  const [attendance, setAttendance] = useState<Record<string, { clockIn: string | null; clockOut: string | null }>>({});
  const [hasNext, setHasNext] = useState(false);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const savingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const period = useMemo(() => getOvertimePeriod(selectedMonth, closingDay), [selectedMonth, closingDay]);

  useEffect(() => { setPage(0); }, [selectedMonth, closingDay, employeeId, status]);
  useEffect(() => {
    let active = true;
    setRows([]);
    setAttendance({});
    setError(null);
    setLoading(true);
    const load = async () => {
      try {
        if (!supabaseClient || !period) throw new Error('対象月と接続を確認してください。');
        let query = supabaseClient.from('attendance_overtime_reports').select(OVERTIME_REPORT_COLUMNS)
          .gte('work_date', period.start).lte('work_date', period.end);
        if (employeeId) query = query.eq('employee_id', employeeId);
        if (status) query = query.eq('status', status);
        const { data, error: readError } = await query.order('work_date', { ascending: false }).order('id', { ascending: false })
          .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
        if (readError) throw readError;
        const rawRows = (data ?? []).slice(0, PAGE_SIZE);
        const reports = rawRows.map(normalizeOvertimeReport);
        if (reports.some((row: OvertimeReport | null) => !row)) throw new Error('報告データの形式を確認できませんでした。');
        const validReports = reports as OvertimeReport[];
        const punchMap: Record<string, { clockIn: string | null; clockOut: string | null }> = {};
        if (validReports.length) {
          const ids = Array.from(new Set(validReports.map(row => row.attendanceId)));
          const { data: punches, error: punchError } = await supabaseClient.from('attendance_records')
            .select('id,clock_in_at,clock_out_at').in('id', ids).limit(PAGE_SIZE);
          if (punchError) throw new Error('報告は取得できましたが、照合する打刻を取得できませんでした。再読み込みしてください。');
          (punches ?? []).forEach((row: any) => { punchMap[String(row.id)] = { clockIn: row.clock_in_at, clockOut: row.clock_out_at }; });
        }
        if (!active) return;
        setRows(validReports);
        setHasNext((data?.length ?? 0) > PAGE_SIZE);
        setNotes(Object.fromEntries(validReports.map(row => [row.id, row.adminNote ?? ''])));
        setAttendance(punchMap);
      } catch (err) {
        if (active) setError(err && typeof err === 'object' && 'message' in err ? String((err as any).message) : '時間外報告を取得できませんでした。');
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    return () => { active = false; };
  }, [supabaseClient, period, employeeId, status, page, reload]);

  const review = async (report: OvertimeReport, newStatus: OvertimeReviewStatus) => {
    if (!supabaseClient || savingRef.current) return;
    const note = (notes[report.id] ?? '').trim();
    if (newStatus === 'cancelled' && !note) { setError('取消理由を管理者メモに入力してください。'); return; }
    if (note.length > 2000) { setError('管理者メモは2,000文字以内で入力してください。'); return; }
    savingRef.current = true;
    setSavingId(report.id);
    setError(null);
    setNotice(null);
    try {
      const parameters = { p_report_id: report.id, p_status: newStatus, p_admin_note: note || null, p_expected_version: report.version };
      let updated: OvertimeReport | null = null;
      let writeError: any = null;
      try {
        const result = await supabaseClient.rpc('review_attendance_overtime', parameters);
        writeError = result.error;
        updated = normalizeOvertimeReport(result.data);
      } catch (err) { writeError = err; }
      const matches = (row: OvertimeReport | null) => row?.id === report.id && row.version === report.version + 1 &&
        row.status === newStatus && (row.adminNote ?? '') === note;
      if (!matches(updated)) {
        const readback = await supabaseClient.from('attendance_overtime_reports').select(OVERTIME_REPORT_COLUMNS).eq('id', report.id).limit(1);
        if (readback.error) throw writeError ?? readback.error;
        updated = normalizeOvertimeReport(readback.data);
        if (!matches(updated)) throw new Error('照合結果の保存を確認できませんでした。別の画面で変更されている場合は再読み込みしてください。');
      }
      setRows(previous => previous.map(row => row.id === report.id ? updated! : row));
      setNotice(`${report.employeeName}・${report.workDate}の照合結果を保存しました。`);
    } catch (err) {
      setError(err && typeof err === 'object' && 'message' in err ? String((err as any).message) : '照合結果を保存できませんでした。');
    } finally {
      savingRef.current = false;
      setSavingId(null);
    }
  };

  return <div className="space-y-5">
    <div><h2 className="text-xl font-bold text-gray-900">時間外勤務の報告・紙との照合</h2>
      <p className="mt-1 text-sm text-gray-600">勤務日・時間帯・理由を紙の届出と確認してください。申告時間は給与の自動計算値と別に記録されます。</p></div>
    <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-4">
      {onMonthChange && <label className="text-sm">対象月<input type="month" aria-label="時間外報告の対象月" value={selectedMonth} onChange={event => onMonthChange(event.target.value)} disabled={Boolean(savingId)} className="mt-1 block rounded border p-2" /></label>}
      <label className="text-sm">従業員<select aria-label="時間外報告の従業員" value={employeeId} disabled={Boolean(savingId)} onChange={event => setEmployeeId(event.target.value)} className="mt-1 block rounded border p-2"><option value="">全従業員</option>{employees.map(employee => <option key={employee.id} value={employee.id}>{employee.name}</option>)}</select></label>
      <label className="text-sm">照合状況<select aria-label="照合状況の絞り込み" value={status} disabled={Boolean(savingId)} onChange={event => setStatus(event.target.value as OvertimeReviewStatus | '')} className="mt-1 block rounded border p-2"><option value="">すべて</option>{Object.entries(OVERTIME_STATUS_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label>
      <button type="button" disabled={loading || Boolean(savingId)} onClick={() => setReload(value => value + 1)} className="rounded border px-4 py-2 disabled:opacity-50">再読み込み</button>
      <span className="text-sm text-gray-600">{period ? `${period.start} ～ ${period.end}` : '対象期間を確認してください'}</span>
    </div>
    {error && <p role="alert" className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</p>}
    {notice && <p role="status" className="rounded bg-green-50 p-3 text-sm text-green-800">{notice}</p>}
    {loading && <p role="status">時間外報告を読み込んでいます…</p>}
    {!loading && !error && rows.length === 0 && <p className="rounded bg-gray-50 p-5 text-gray-600">対象期間・条件に該当する報告はありません。</p>}
    {rows.map(report => <article key={report.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap justify-between gap-2"><h3 className="font-bold text-gray-900">{report.employeeName} <span className="font-normal text-gray-600">（{report.employeeCode}）</span> ／ {report.workDate}</h3><span className={`rounded px-2 py-1 text-sm ${report.status === 'reviewed' ? 'bg-green-100 text-green-900' : report.status === 'needs_review' ? 'bg-red-100 text-red-900' : 'bg-amber-100 text-amber-900'}`}>{OVERTIME_STATUS_LABELS[report.status]}</span></div>
      <dl className="grid gap-2 text-sm md:grid-cols-2">
        <div><dt className="text-gray-500">打刻（原時刻）</dt><dd>{formatOvertimeDateTime(attendance[report.attendanceId]?.clockIn ?? null)} ～ {formatOvertimeDateTime(attendance[report.attendanceId]?.clockOut ?? null)}</dd></div>
        <div><dt className="text-gray-500">申告した時間帯・時間数</dt><dd>{formatOvertimeDateTime(report.startAt)} ～ {formatOvertimeDateTime(report.endAt)} <strong>{formatOvertimeMinutes(report.durationMinutes)}</strong></dd></div>
        <div className="md:col-span-2"><dt className="text-gray-500">理由</dt><dd className="whitespace-pre-wrap break-words">{report.reason}</dd></div>
        <div><dt className="text-gray-500">提出日時 ／ 最終更新</dt><dd>{formatOvertimeDateTime(report.submittedAt)} ／ {formatOvertimeDateTime(report.updatedAt)}</dd></div>
        <div><dt className="text-gray-500">照合日時</dt><dd>{formatOvertimeDateTime(report.reviewedAt)}</dd></div>
      </dl>
      <label className="mt-3 block text-sm text-gray-700">管理者メモ<textarea aria-label={`${report.employeeName} ${report.workDate}の管理者メモ`} value={notes[report.id] ?? ''} onChange={event => setNotes(previous => ({ ...previous, [report.id]: event.target.value }))} disabled={Boolean(savingId)} maxLength={2000} rows={2} className="mt-1 w-full rounded border border-gray-300 p-2" /></label>
      <div className="mt-3 flex flex-wrap gap-2">{(['reviewed', 'needs_review', 'pending', 'cancelled'] as OvertimeReviewStatus[]).map(nextStatus => <button type="button" key={nextStatus} disabled={Boolean(savingId)} onClick={() => review(report, nextStatus)} className={`rounded px-3 py-2 text-sm disabled:opacity-50 ${nextStatus === 'reviewed' ? 'bg-blue-600 text-white' : 'border border-gray-300'}`}>{savingId === report.id ? '保存中…' : nextStatus === 'reviewed' ? '紙と照合して確認済みにする' : nextStatus === 'cancelled' ? '報告を取り消す（理由必須）' : `${OVERTIME_STATUS_LABELS[nextStatus]}として保存`}</button>)}</div>
    </article>)}
    <div className="flex items-center gap-3"><button type="button" disabled={page === 0 || loading || Boolean(savingId)} onClick={() => setPage(value => value - 1)} className="rounded border px-3 py-2 disabled:opacity-50">前へ</button><span className="text-sm">{page + 1}ページ</span><button type="button" disabled={!hasNext || loading || Boolean(savingId)} onClick={() => setPage(value => value + 1)} className="rounded border px-3 py-2 disabled:opacity-50">次へ</button></div>
  </div>;
};

export default OvertimeReviewTab;
